import { Reveal } from "../landing/Reveal";
import type { UseCaseConfig } from "../../use-cases/types";

interface CaseStudyHeroProps {
  hero: UseCaseConfig["hero"];
}

/**
 * Editorial hero for any use-case page. Pitches the IDEA of a Year in Review
 * for a specific audience — generic, not anchored to a real customer. The
 * byline row underneath is about the FORMAT (phone-first, brand-able), not
 * a customer.
 */
export const CaseStudyHero = ({ hero }: CaseStudyHeroProps) => (
  <section className="bg-fwd-soft pt-[calc(var(--site-header-h,4rem)+4rem)] pb-20 md:pt-[calc(var(--site-header-h,4rem)+6rem)] md:pb-28 px-6 md:px-16 lg:px-24 relative">
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.04]"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="caseGrid"
          x="0"
          y="0"
          width="36"
          height="36"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 36 0 L 0 0 0 36"
            fill="none"
            stroke="#111118"
            strokeWidth="0.6"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#caseGrid)" />
    </svg>

    <div className="max-w-5xl mx-auto relative z-10">
      <Reveal immediate>
        <div className="flex items-center gap-3 mb-10">
          <span className="inline-flex items-center gap-2 bg-fwd-ink text-fwd-yellow text-[0.65rem] font-bold tracking-[0.22em] uppercase px-3 py-1.5 rounded-sm">
            {hero.eyebrowLabel}
          </span>
          <span className="text-fwd-mid text-[0.7rem] font-semibold tracking-[0.18em] uppercase">
            {hero.subhead}
          </span>
        </div>
      </Reveal>

      <Reveal immediate delay={0.05}>
        <h1 className="font-display text-[clamp(2.6rem,5vw,5rem)] font-normal leading-[1.04] text-fwd-ink mb-8 max-w-4xl tracking-tight">
          {hero.headlineLead}
          <span className="italic text-fwd-red">{hero.headlineEmphasis}</span>
        </h1>
      </Reveal>

      <Reveal immediate delay={0.15}>
        <p className="font-serif text-fwd-ink/75 text-xl md:text-2xl leading-[1.55] max-w-3xl mb-5">
          {hero.body}
        </p>
      </Reveal>

      <Reveal immediate delay={0.2}>
        <p className="text-fwd-mid text-sm font-medium tracking-wide">
          {hero.byline}
        </p>
      </Reveal>
    </div>
  </section>
);
